<template>
  <Widget class="fill-height">
    <v-card class="pa-5 fill-height widget-card" flat>
      <v-container class="d-flex flex-column pa-0" style="height: 100%">
        <v-row class="flex-grow-0">
          <v-col>
            <h2 class="text-left mt-3 mb-1">Response status</h2>
            <p class="explanatory-text">
              Overview of the questionnaire response status
            </p>
          </v-col>
        </v-row>
        <v-row align="center" class="mt-4 flex-grow-1">
          <v-col class="text-center progress-circle">
            <v-progress-circular
              v-model="statsStore.completedPercentage"
              size="180"
              width="8"
              :color="getCircleColor('EntireSupplyChain')"
            >
              <v-progress-circular
                v-model="statsStore.completedPercentageTier1"
                size="160"
                width="8"
                :color="getCircleColor('Tier1Suppliers')"
              >
                <v-progress-circular
                  v-model="statsStore.completedPercentageBusinessPartners"
                  size="140"
                  width="8"
                  :color="getCircleColor('BusinessPartners')"
                />
              </v-progress-circular>
            </v-progress-circular>
          </v-col>
          <v-col class="d-flex flex-column ml-4" cols="12" sm="6">
            <v-row class="icon-row">
              <v-col class="icon-col">
                <div class="mt-1 text-right circle" is="EntireSupplyChain" />
              </v-col>
              <v-col>
                <strong class="prepend-circle">
                  {{ statsStore.completedPercentage }} %</strong
                >
                <p>Entire supply chain</p>
                <p>
                  {{ statsStore.completedUnits }} out of
                  {{ statsStore.totalNumberOfUnits }} units
                </p>
              </v-col>
            </v-row>
            <v-row class="icon-row">
              <v-col class="icon-col">
                <div class="mt-1 text-right circle" is="Tier1Suppliers" />
              </v-col>
              <v-col>
                <strong class="prepend-circle">
                  {{ statsStore.completedPercentageTier1 }} %</strong
                >
                <p>Tier 1 suppliers</p>
                <p>
                  {{ statsStore.completedUnitsTier1 }} out of
                  {{ statsStore.totalNumberOfUnitsTier1 }} units
                </p>
              </v-col>
            </v-row>
            <v-row class="icon-row">
              <v-col class="icon-col">
                <div class="mt-1 text-right circle" is="BusinessPartners" />
              </v-col>
              <v-col>
                <strong class="prepend-circle">
                  {{ statsStore.completedPercentageBusinessPartners }}
                  %</strong
                >
                <p>Business partners</p>
                <p>
                  {{ statsStore.completedUnitsBusinessPartners }} out of
                  {{ statsStore.totalNumberOfUnitsBusinessPartners }} units
                </p>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
        <v-row class="text-right flex-grow-0">
          <v-col>
            <PActionButton
              variant="text"
              appkit-icon="icon-right-chevron-fill"
              :icon-size="'xs'"
              :append-icon="true"
              color="var(--blue-violet-2)"
              @click="routeToQuestionnairePage"
              >View all</PActionButton
            >
          </v-col>
        </v-row>
      </v-container>
    </v-card>
  </Widget>
</template>

<script setup lang="ts">
import PActionButton from "@/components/custom/buttons/PActionButton.vue";
import { useStatsStore } from "@/stores/statsStore";
import Widget from "@/views/dashboard-view/Widget.vue";
import { useRouter } from "vue-router";
import { RouterPath } from "@/models/enum/routerPath";
import { useQuestionnairesStore } from "@/stores/questionnaires";
import { useSenderStore } from "@/stores/sender";

const router = useRouter();
const statsStore = useStatsStore();
const questionnairesStore = useQuestionnairesStore();
const senderStore = useSenderStore();

function getCircleColor(businessRelationship: string) {
  switch (businessRelationship) {
    case "EntireSupplyChain":
      return "var(--yellow)";
    case "Tier1Suppliers":
      return "var(--digital-rose)";
    case "BusinessPartners":
      return "var(--red-orange)";
  }
}

function routeToQuestionnairePage() {
  senderStore.setOwnerParams(questionnairesStore.selectedQuestionnaire);
  router.push(RouterPath.MyQuestionnaireStatus);
}
</script>

<style scoped lang="scss">
.widget-card {
  border-radius: 6px;
}
.progress-circle {
  :deep(.v-progress-circular__overlay) {
    stroke-linecap: round;
  }
}
.explanatory-text {
  font-size: 14px;
}

.circle {
  height: 16px;
  width: 16px;
  border-radius: 50%;
}

.circle[is="EntireSupplyChain"] {
  background-color: v-bind(getCircleColor("EntireSupplyChain"));
}
.circle[is="Tier1Suppliers"] {
  background-color: v-bind(getCircleColor("Tier1Suppliers"));
}
.circle[is="BusinessPartners"] {
  background-color: v-bind(getCircleColor("BusinessPartners"));
}

.icon-row {
  width: 100%;
}
.icon-col {
  max-width: 30px;
}
</style>
